import React, { useState, useEffect } from 'react';
import { Alert, AlertTitle, Box, Collapse, Typography } from '@mui/material';
import { systemNotificationAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

interface SystemNotification {
  id: string;
  title: string;
  message: string;
  type: 'informational' | 'warning' | 'emergency';
  startDate: string;
}

const STORAGE_KEY = 'acknowledgedSystemNotifications';

const getAcknowledged = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
};

const severityMap = {
  informational: 'info' as const,
  warning: 'warning' as const,
  emergency: 'error' as const,
};

const SystemNotificationBanner: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState<SystemNotification[]>([]);
  const [acknowledged, setAcknowledged] = useState<string[]>([]);

  useEffect(() => {
    setAcknowledged(getAcknowledged());
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchNotifications = async () => {
      try {
        const res = await systemNotificationAPI.getActive();
        setNotifications(res.data);
      } catch {
        // Silently ignore — notification banner is non-critical
      }
    };

    fetchNotifications();
  }, [isAuthenticated]);

  const handleDismiss = (id: string) => {
    const updated = [...acknowledged, id];
    setAcknowledged(updated);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    } catch {
      // localStorage not available
    }
  };

  const visible = notifications.filter((n) => !acknowledged.includes(n.id));

  if (visible.length === 0) return null;

  return (
    <Box sx={{ mb: 2 }}>
      {visible.map((n) => (
        <Collapse key={n.id} in={!acknowledged.includes(n.id)}>
          <Alert
            severity={severityMap[n.type]}
            onClose={() => handleDismiss(n.id)}
            sx={{ mb: 1 }}
          >
            <AlertTitle sx={{ fontWeight: 700 }}>{n.title}</AlertTitle>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {n.message}
            </Typography>
          </Alert>
        </Collapse>
      ))}
    </Box>
  );
};

export default SystemNotificationBanner;
